import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getCurrentSeason, getSeasonById } from '../utils/seasonalTheme';

const ThemeContext = createContext(null);

// Tokens light & dark
const THEMES = {
  light: {
    '--bg':        '#FDFAF6',
    '--surface':   '#FFFFFF',
    '--surface2':  '#F8F5F1',
    '--border':    '#EAE4DC',
    '--border-md': '#D5CDBE',
    '--ink':       '#1C1612',
    '--muted':     '#8A7E72',
    '--subtle':    '#B5A99A',
    '--orange':    '#F04E1A',
    '--orange-d':  '#C83E12',
    '--orange-pale':'#FFF3EE',
    '--yellow':    '#F5C842',
    '--green':     '#16A34A',
    '--red':       '#DC2626',
    '--blue':      '#2563EB',
    '--purple':    '#7C3AED',
    '--header-bg': 'rgba(253,250,246,.92)',
    '--card-shadow':'0 1px 4px rgba(0,0,0,.06)',
    '--card-shadow-hover':'0 6px 20px rgba(0,0,0,.10)',
  },
  dark: {
    '--bg':        '#141210',
    '--surface':   '#1E1B18',
    '--surface2':  '#252220',
    '--border':    '#2E2A26',
    '--border-md': '#3D3830',
    '--ink':       '#F0EBE4',
    '--muted':     '#9E9488',
    '--subtle':    '#6B6258',
    '--orange':    '#FF6B3D',
    '--orange-d':  '#E85520',
    '--orange-pale':'#2A1A12',
    '--yellow':    '#F5C842',
    '--green':     '#22C55E',
    '--red':       '#F87171',
    '--blue':      '#60A5FA',
    '--purple':    '#A78BFA',
    '--header-bg': 'rgba(20,18,16,.92)',
    '--card-shadow':'0 1px 4px rgba(0,0,0,.3)',
    '--card-shadow-hover':'0 6px 20px rgba(0,0,0,.5)',
  },
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('bulletheque_theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Ambiance saisonnière : 'auto' (selon la date), 'off', ou l'id d'une occasion (aperçu)
  const [seasonPref, setSeasonPrefState] = useState(() => {
    try { return localStorage.getItem('bulletheque_season_pref') || 'auto'; } catch { return 'auto'; }
  });
  const setSeasonPref = (v) => {
    setSeasonPrefState(v);
    try { localStorage.setItem('bulletheque_season_pref', v); } catch {}
  };
  const [fxLevel, setFxLevelState] = useState(() => {
    try { return localStorage.getItem('bulletheque_fx_level') || 'normal'; } catch { return 'normal'; }
  });
  const setFxLevel = (v) => {
    setFxLevelState(v);
    try { localStorage.setItem('bulletheque_fx_level', v); } catch {}
  };
  const season = useMemo(() => {
    if (seasonPref === 'off') return null;
    if (seasonPref === 'auto') return getCurrentSeason();
    return getSeasonById(seasonPref) || getCurrentSeason();
  }, [seasonPref]);

  useEffect(() => {
    const root = document.documentElement;
    const tokens = { ...THEMES[theme], '--bg-image': 'none', ...(season?.p?.[theme] || {}) };
    Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));
    root.setAttribute('data-theme', theme);
    localStorage.setItem('bulletheque_theme', theme);
  }, [theme, season]);

  const toggle = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, toggle, isDark: theme === 'dark', season, seasonPref, setSeasonPref, fxLevel, setFxLevel }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

// Helper : lire une CSS variable au runtime
export const tv = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
